import React, { useState, useEffect, useCallback } from 'react';
import { generateTxnRef, computeKeyTest, formatUSD, KTT_EMAIL, KTT_CODE, COMPANY, todayISO, toUSD, getFXRate } from './utils/keyGenerator';
import Dashboard from './pages/Dashboard';
import Deposit from './pages/DepositModule';
import Ledger from './pages/Ledger';
import SendAPI from './pages/SendViaAPI';
import SendKTT from './pages/SendViaKTT';
import SendViaRTGS from './pages/SendViaRTGS';
import POBOSwift from './pages/POBOSwift';
import SwiftOperations from './pages/SwiftOperations';
import LiveTracker from './pages/LiveTracker';
import AccountsPage from './pages/AccountsPage';
import Reports from './pages/Reports';
import BotCertificate from './pages/BotCertificate';
import APIKeys from './pages/APIKeys';
import Settings from './pages/Settings';
import SafetyCenter from './pages/SafetyCenter';
import TransactionSlip from './pages/TransactionSlip';
import { downloadSlipPDF } from './utils/pdfUtils';

const STORAGE_WALLET = 'ktt_wallet';
const STORAGE_TXN = 'ktt_transactions';

function loadTransactions() {
  try {
    const stored = localStorage.getItem(STORAGE_TXN);
    if (!stored) return [];
    const txns = JSON.parse(stored);
    let migrated = false;
    txns.forEach(tx => {
      if (tx.debitAmount && tx.currency && tx.currency !== 'USD' && !tx.originalCurrency) {
        const fxRate = getFXRate(tx.currency);
        if (fxRate > 1) {
          tx.originalAmount = tx.debitAmount;
          tx.originalCurrency = tx.currency;
          tx.fxRate = fxRate;
          tx.debitAmount = toUSD(tx.originalAmount, tx.currency);
          tx.description = (tx.description || '') + ` (FX corrected: ${tx.originalAmount} ${tx.currency} @ ${fxRate})`;
          migrated = true;
        }
      }
    });
    if (migrated) {
      localStorage.setItem(STORAGE_TXN, JSON.stringify(txns));
    }
    return txns;
  } catch { return []; }
}

function calcWalletFromTxns(txns) {
  let balance = 0;
  const reversed = [...txns].reverse();
  reversed.forEach(tx => {
    if (tx.creditAmount) balance += tx.creditAmount;
    if (tx.debitAmount) balance -= tx.debitAmount;
  });
  return { balance, currency: 'USD' };
}

// Detect if running embedded inside the BOS Admin Portal iframe
const isEmbedded = window.self !== window.top;

export default function App() {
  const [page, setPage] = useState("dashboard");
  const [transactions, setTransactions] = useState(() => loadTransactions());
  const [wallet, setWallet] = useState(() => calcWalletFromTxns(loadTransactions()));
  const [slip, setSlip] = useState(null);
  const autoSlipRef = React.useRef(null);
  const [accounts, setAccounts] = useState([]);

  // Auto-generate (download) a slip PDF the moment a new receipt is produced,
  // so every dispatched message is recorded to file automatically.
  useEffect(() => {
    if (slip && slip.txnRef && autoSlipRef.current !== slip.txnRef) {
      autoSlipRef.current = slip.txnRef;
      try { downloadSlipPDF(slip); } catch (e) { /* user can still use the button */ }
    }
  }, [slip]);
  const [preSelectedAccount, setPreSelectedAccount] = useState(null); // {iban, holderName} for send-from-account

  // Fetch wallet balance from backend (source of truth)
  const syncWallet = useCallback(() => {
    fetch('/api/wallet').then(r => r.ok ? r.json() : null).then(data => {
      if (data && typeof data.balance === 'number') setWallet(w => ({ ...w, balance: data.balance }));
    }).catch(() => {});
  }, []);

  // Fetch accounts from backend
  const fetchAccounts = useCallback(() => {
    fetch('/api/accounts').then(r => r.ok ? r.json() : []).then(setAccounts).catch(() => {});
  }, []);

  // Fetch transactions from backend — backend is always source of truth
  const syncTransactions = useCallback(() => {
    fetch('/api/transactions').then(r => r.ok ? r.json() : null).then(data => {
      if (data && Array.isArray(data)) {
        setTransactions(data);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    syncWallet();
    fetchAccounts();
    syncTransactions();
    const interval = setInterval(() => { syncWallet(); fetchAccounts(); syncTransactions(); }, 5000);
    return () => clearInterval(interval);
  }, [syncWallet, fetchAccounts, syncTransactions]);

  // Listen for navigation commands from the BOS Admin Portal parent frame
  useEffect(() => {
    function handleParentNav(ev) {
      if (ev.data && ev.data.type === 'KTT_NAV' && ev.data.page) {
        setPage(ev.data.page);
      }
    }
    window.addEventListener('message', handleParentNav);
    return () => window.removeEventListener('message', handleParentNav);
  }, []);

  // All hooks MUST be above any conditional return
  useEffect(() => {
    localStorage.setItem(STORAGE_TXN, JSON.stringify(transactions));
    localStorage.setItem(STORAGE_WALLET, JSON.stringify(wallet));
  }, [transactions, wallet]);

  const addTransaction = useCallback((tx) => {
    const dateStr = todayISO();
    const amount = tx.creditAmount || tx.debitAmount || 0;
    const keyTest = tx.keyTest || computeKeyTest(amount, tx.valueDate || dateStr);

    const newTx = {
      ...tx,
      id: tx.id || generateTxnRef(tx.type || "TRF"),
      date: new Date().toISOString(),
      status: "SETTLED",
      keyTest,
    };

    setTransactions(prev => {
      const prevBalance = prev.length > 0 ? (prev[0].balanceAfter ?? 0) : 0;
      if (tx.creditAmount) {
        newTx.balanceAfter = prevBalance + tx.creditAmount;
      } else if (tx.debitAmount) {
        newTx.balanceAfter = prevBalance - tx.debitAmount;
      } else {
        newTx.balanceAfter = prevBalance;
      }
      return [newTx, ...prev];
    });

    if (tx.creditAmount) {
      setWallet(w => ({ ...w, balance: w.balance + tx.creditAmount }));
    }
    if (tx.debitAmount) {
      setWallet(w => ({ ...w, balance: w.balance - tx.debitAmount }));
    }

    // Sync from backend after transaction
    setTimeout(() => { syncWallet(); fetchAccounts(); }, 500);

    return newTx;
  }, [syncWallet, fetchAccounts]);

  // Navigate to a payment page with pre-selected source account
  const navigateToSend = (method, accountIban, accountHolder) => {
    setPreSelectedAccount({ iban: accountIban, holderName: accountHolder });
    setPage(method); // 'api', 'ktt', 'rtgs', 'pobo'
  };

  const handleResetAll = async () => {
    if (!window.confirm('This will clear ALL accounts, transactions, and balances. Are you sure?')) return;
    try {
      await fetch('/api/reset', { method: 'POST' });
    } catch {}
    localStorage.removeItem(STORAGE_TXN);
    localStorage.removeItem(STORAGE_WALLET);
    setTransactions([]);
    setWallet({ balance: 0, currency: 'USD' });
    setAccounts([]);
    setPage('dashboard');
  };

  const totalCredit = transactions.filter(t => t.creditAmount).reduce((s, t) => s + (t.creditAmount || 0), 0);
  const totalDebit = transactions.filter(t => t.debitAmount).reduce((s, t) => s + (t.debitAmount || 0), 0);

  const navSections = [
    {
      title: "Main",
      items: [
        { id: "dashboard", icon: "\u25C8", label: "Dashboard" },
        { id: "accounts", icon: "\u2395", label: "Accounts" },
        { id: "deposit", icon: "\u25B2", label: "Deposit Funds" },
        { id: "ledger", icon: "\u2261", label: "Ledger" },
      ]
    },
    {
      title: "Payments",
      items: [
        { id: "api", icon: "\u25C7", label: "Send via API" },
        { id: "ktt", icon: "\u2726", label: "Send via KTT" },
        { id: "rtgs", icon: "\u2302", label: "Send via RTGS" },
        { id: "pobo", icon: "\u2B21", label: "POBO SWIFT" },
        { id: "swift", icon: "\u2316", label: "SWIFT Operations" },
        { id: "tracker", icon: "\u25C9", label: "Live Tracker" },
      ]
    },
    {
      title: "Compliance",
      items: [
        { id: "safety", icon: "\u26E8", label: "Safety Center" },
      ]
    },
    {
      title: "Reports",
      items: [
        { id: "reports", icon: "\u25A1", label: "Reports & Export" },
        { id: "apikeys", icon: "\u26BF", label: "API Keys" },
        { id: "settings", icon: "\u2699", label: "Settings" },
        { id: "certificate", icon: "\u2318", label: "Bot Certificate" },
      ]
    }
  ];

  const pageTitles = {
    dashboard: "Wallet Dashboard",
    accounts: "Accounts \u2014 ISO 13616 IBAN",
    deposit: "Deposit Funds",
    ledger: "Transaction Ledger",
    api: "Send via API",
    ktt: "Send via KTT \u2014 ISO 20022",
    rtgs: "Send via RTGS \u2014 Real-Time Gross Settlement",
    pobo: "POBO SWIFT \u2014 Redeem & Payout",
    swift: "SWIFT Operations Center",
    tracker: "Live Transaction Tracker",
    reports: "Reports & Export",
    apikeys: "API Keys & Authentication",
    settings: "Payment Gateway Settings",
    safety: "Safety Center \u2014 Bank-Grade Fraud Protection",
    certificate: "Bot Certificate",
  };

  return (
    <>
      <div className={`app${isEmbedded ? ' embedded' : ''}`}>
        {/* Sidebar — hidden when embedded inside BOS Admin Portal */}
        {!isEmbedded && (
          <div className="sidebar">
            <div className="sidebar-logo">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src="/USDDWIN_logo_100.png" alt="Dwin" style={{ width: 42, height: 42, objectFit: 'contain' }} />
                <div>
                  <div className="company">Dwin</div>
                  <div className="subtitle">dwingateway.live</div>
                </div>
              </div>
            </div>
            <nav className="nav">
              {navSections.map(section => (
                <React.Fragment key={section.title}>
                  <div className="nav-section">{section.title}</div>
                  {section.items.map(item => (
                    <div key={item.id} className={`nav-item ${page === item.id ? "active" : ""}`} onClick={() => setPage(item.id)}>
                      <span className="icon">{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </nav>
            <div className="sidebar-footer">
              <div className="ktt-badge">
                <div className="label">KTT CODE</div>
                <div className="code">{KTT_CODE}</div>
              </div>
            </div>
          </div>
        )}

        <div className="main">
          {/* Topbar — hidden when embedded; outer portal shows title */}
          {!isEmbedded && (
            <div className="topbar no-print">
              <div className="topbar-title">{pageTitles[page]}</div>
              <div className="topbar-right">
                <span className="topbar-email">{KTT_EMAIL}</span>
                <span className="balance-pill">{formatUSD(wallet.balance)}</span>
                <button className="btn btn-outline btn-sm" onClick={handleResetAll} title="Clear all data">Reset</button>
              </div>
            </div>
          )}
          <div className="content">
            {page === "dashboard" && <Dashboard wallet={wallet} transactions={transactions} totalCredit={totalCredit} totalDebit={totalDebit} setPage={setPage} accounts={accounts} />}
            {page === "accounts" && <AccountsPage accounts={accounts} onRefresh={fetchAccounts} allTransactions={transactions} />}
            {page === "deposit" && <Deposit addTransaction={addTransaction} wallet={wallet} accounts={accounts} preSelectedAccount={preSelectedAccount} onAccountUsed={() => setPreSelectedAccount(null)} />}
            {page === "ledger" && <Ledger transactions={transactions} setSlip={setSlip} />}
            {page === "api" && <SendAPI wallet={wallet} addTransaction={addTransaction} setSlip={setSlip} accounts={accounts} preSelectedAccount={preSelectedAccount} onAccountUsed={() => setPreSelectedAccount(null)} />}
            {page === "ktt" && <SendKTT wallet={wallet} addTransaction={addTransaction} setSlip={setSlip} accounts={accounts} preSelectedAccount={preSelectedAccount} onAccountUsed={() => setPreSelectedAccount(null)} />}
            {page === "rtgs" && <SendViaRTGS wallet={wallet} addTransaction={addTransaction} setSlip={setSlip} accounts={accounts} preSelectedAccount={preSelectedAccount} onAccountUsed={() => setPreSelectedAccount(null)} />}
            {page === "pobo" && <POBOSwift wallet={wallet} addTransaction={addTransaction} setSlip={setSlip} accounts={accounts} preSelectedAccount={preSelectedAccount} onAccountUsed={() => setPreSelectedAccount(null)} />}
            {page === "swift" && <SwiftOperations />}
            {page === "tracker" && <LiveTracker transactions={transactions} />}
            {page === "reports" && <Reports transactions={transactions} wallet={wallet} totalCredit={totalCredit} totalDebit={totalDebit} />}
            {page === "apikeys" && <APIKeys />}
            {page === "settings" && <Settings />}
            {page === "safety" && <SafetyCenter />}
            {page === "certificate" && <BotCertificate wallet={wallet} transactions={transactions} />}
          </div>
        </div>
      </div>

      {slip && (
        <div className="overlay">
          <div className="modal">
            <div className="modal-header no-print">
              <span className="card-title">Transaction Receipt</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-gold btn-sm" onClick={() => downloadSlipPDF(slip)}>Download PDF</button>
                <button className="btn btn-outline btn-sm" onClick={() => window.print()}>Print</button>
                <button className="btn btn-outline btn-sm" onClick={() => setSlip(null)}>Close</button>
              </div>
            </div>
            <div className="modal-body">
              <TransactionSlip slip={slip} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
